import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, of, Subscription } from 'rxjs';
import { filter, last, map } from 'rxjs/operators';
import { HisBackendService } from '../../core/service/backend/his-backend.service';
import { HisUploadConfiguration } from '../model/his-upload-configuration';
import { HisUploadFile } from '../model/his-upload-file';
import { ResourceFeedback } from '../model/resource-feedback';
import { UploadFieldConfigResponse } from '../model/upload-field-config-response';
import { UploadFieldRequest } from '../model/upload-field-request';
import * as _ from 'lodash';
import { HisNavigationService } from '../../core/service/base/his-navigation.service';
import { v4 as newUUID } from 'uuid';

@Injectable({providedIn: 'root'})
export class HisFileUploadService {
    private backendService = inject( HisBackendService );
    private navigationService = inject( HisNavigationService );

    private ongoingFeedbackRequests = new Map<string, Subscription>();

    public getUploadFieldConfig(
        configKey: string, javascriptcontext?: string, contextobject?: Map<string, any> ): Observable<UploadFieldConfigResponse> {

        let request = new UploadFieldRequest();
        request.configKey = configKey;
        request.javascriptContext = javascriptcontext;
        request.contextObject = contextobject;

        return this.backendService.postData( 'api/v1/cs/sys/fileupload/getUploadFieldConfig', request ).pipe(
            filter( ( data ) => !!data ),
            map( this.transformToInternalModel )
        );
    }

    private transformToInternalModel( data: {
        configKey: string;
        multipleUpload: boolean;
        label: string;
        required: boolean;
        readonly: boolean;
        helptextIcon: string;
        helptextAbove: string;
        helptextBelow: string;
        fileLimit: number;
        sizeLimit: number;
        allowedExtensions: Set<string>
        allowedContentTypes: Set<string>
        showCategoryIfUnique: boolean;
        allowComment: boolean;
        documentMetadataCategoryIds: Set<number>;
        css: string;
        uploadForLargeFiles: boolean;
        sourceTypes: Set<string>;
        localMediaCategories: Set<string>;
    } ): UploadFieldConfigResponse {
        let result = new UploadFieldConfigResponse();
        result.allowComment = data.allowComment;
        result.configKey = data.configKey;
        result.documentMetadataCategoryIds = data.documentMetadataCategoryIds;
        result.fileLimit = data.fileLimit;
        result.helptextAbove = data.helptextAbove;
        result.helptextBelow = data.helptextBelow;
        result.helptextIcon = data.helptextIcon;
        result.label = data.label;
        result.multipleUpload = data.multipleUpload;
        result.readonly = data.readonly;
        result.required = data.required;
        result.showCategoryIfUnique = data.showCategoryIfUnique;
        result.sizeLimit = data.sizeLimit;
        result.css = data.css;
        result.uploadForLargeFiles = data.uploadForLargeFiles;
        result.sourceTypes = data.sourceTypes;
        result.localMediaCategories = data.localMediaCategories;

        let allowedExtensions = [...data.allowedExtensions || []]
            .map( s => {
                if ( !s || s.startsWith( '.' ) ) {
                    return s;
                }
                return '.' + s;
            } );

        const separator = ', ';
        result.accept = [...data.allowedContentTypes || [], allowedExtensions].join( separator );
        return result;
    }

    public getUploadFiles( files: File[] ): Observable<HisUploadFile[]> {
        if ( !files ) {
            return of( [] );
        }

        if ( files && files.length === 0 ) {
            return of( [] );
        }

        // Vorsicht: ein Array von Observables, für jedes uploadFile eines: [Obs1<HisUploadFile>, Obs2<HisUploadFile>]
        // let constArray: Observable<HisUploadFile>[] = [];
        let fileObservables = files.map( this.fileToHisUploadFile );

        const filesArrayObservable = forkJoin( fileObservables );
        return filesArrayObservable.pipe( last() );
    }

    public tusUploadToHisUploadFile( upload: { url: string, options: { metadata: { filename: string, filetype: string } } } ): HisUploadFile {
        let hisUploadFile = new HisUploadFile();
        hisUploadFile.uuid = upload.url.substring( upload.url.lastIndexOf( '/' ) + 1 );
        hisUploadFile.name = upload.options.metadata.filename;
        hisUploadFile.contentType = upload.options.metadata.filetype;
        hisUploadFile.supportsStreaming = upload.options.metadata.filetype.startsWith( 'video/' ) || upload.options.metadata.filetype.startsWith( 'audio/' );
        hisUploadFile.downloadUrl = `${this.navigationService.getQisserverUrl()}/rds?state=reviewupload&docId=${hisUploadFile.uuid}${hisUploadFile.supportsStreaming ? '&docviewer=true' : ''}`;
        return hisUploadFile;
    }

    public fileToHisUploadFile( file: File ): Observable<HisUploadFile> {
        let reader = new FileReader();
        let obs = new BehaviorSubject<HisUploadFile>( null );

        reader.onload = () => {
            // You'll get the base64 string here
            let uploadFile = new HisUploadFile();
            uploadFile.uuid = newUUID();

            // Das Problem ist, dass wenn wir des Bytearray von HisResource nach HisFileUpload übertragen,
            // wird nur das Byte-Array übertragen, ohne den Header.
            // Dadurch bekommen wir eine Inkonsistenz.
            // Lösung: wir halten HisUploadFile.base64 auch ohne Header.
            // Zum Anzeigen und Herunterladen rufen wir HisUploadFile.dataUrl auf.
            let readerResult = reader.result as string;
            let [, base64Data] = readerResult.split( ',' );
            uploadFile.base64 = base64Data;

            uploadFile.name = file.name;
            uploadFile.lastModified = file.lastModified ? file.lastModified.toString() : null;
            uploadFile.contentType = file.type;
            obs.next( uploadFile );
            obs.complete();
        };

        reader.onerror = ( error ) => {
            console.log( 'Error reading file :', error );
        };

        reader.readAsDataURL( file );
        return obs;
    }

    public getUploadFieldInstances(
        configKey: string, javascriptcontext?: string, contextobject?: Map<string, any> ): Observable<string[]> {

        let request = new UploadFieldRequest();
        request.configKey = configKey;
        request.javascriptContext = javascriptcontext;
        request.contextObject = contextobject;
        return this.backendService.postData( 'api/v1/cs/sys/fileupload/getUploadFieldInstances', request );
    }

    public getIsMaxUploadsReached( multiple: boolean, fileLimit: number, model: HisUploadFile[] ): boolean {
        if ( !model ) {
            return false;
        }

        if ( multiple && !fileLimit ) {
            return false;
        }

        if ( multiple && fileLimit && model.length >= fileLimit ) {
            return true;
        }

        if ( !multiple && ( model.length >= 1 ) ) {
            return true;
        }

        return false;
    }

    /**
     * @param currentFiles
     * @param configuration
     * @param model
     */
    public limitNumberOfFilesToTheConfuguredFileLimit( currentFiles: FileList | File[], configuration: HisUploadConfiguration, model: HisUploadFile[] ): File[] {
        let uploadedFiles: File[] = [];
        if ( !currentFiles || !configuration || !model ) {
            return uploadedFiles;
        }

        if ( configuration.fileLimit ) {
            // wenn es ein FileLimit gibt und wenn die gerade hochgeladenen Files (z. B. 3 Files auf einmal)
            // größer als das FileLimit sind
            // oder wenn model plus currentFiles größer als das FileLimit sind
            const currentFilesExceedFileLimit = Array.from( currentFiles ).length > configuration.fileLimit;
            const totalNumberOfFilesExceedFileLimit = model.length + Array.from( currentFiles ).length;

            if ( currentFilesExceedFileLimit || totalNumberOfFilesExceedFileLimit ) {
                // Frage: könnte man mit fileLimit an p-uploadFiles diesen Fall verhindern? Nein
                // In JSF wird das letzte, also überzählige File abgeschnitten.
                // Es gibt keine Fehlermeldung. Die Eingabemöglichkt verschwindet.
                // Die files rutschen nach oben, es sieht so aus als würde die ganze Komponente ausgebledet werden.
                // wir nicht gerendert, wenn cc.maxUploadsReached
                const max = configuration.fileLimit - model.length;

                uploadedFiles = Array.from( currentFiles ).splice( 0, max );
                return uploadedFiles;
            }
        }

        uploadedFiles = Array.from( currentFiles );
        return uploadedFiles;
    }

    public getDownloadUrl( uploadFile: HisUploadFile ): string {
        if ( !uploadFile.id && !!uploadFile.base64 ) {
            return `data:${uploadFile.contentType};base64,${uploadFile.base64}`;
        }

        return `${this.navigationService.getQisserverUrl()}/rds?state=docdownload&docId=${uploadFile.uuid}${uploadFile.supportsStreaming ? '&docviewer=true' : ''}`;
    }

    public provideFeedback( configKey: string, uploadFile: HisUploadFile, javascriptcontext?: string, contextobject?: Map<string, any> ): Observable<ResourceFeedback[]> {
        const request = {
            uploadFieldConfigKey: configKey,
            resource: uploadFile,
            contextObjects: contextobject,
            javascriptContext: javascriptcontext
        };

        return this.backendService.postData( 'api/v1/cs/sys/fileupload/validation/provideFeedback', request, { blockUI: false } );
    }

    public batchProvideFeedback( configKey: string, uploadFiles: HisUploadFile[], javascriptcontext?: string, contextobject?: Map<string, any> ): Observable<Map<string, ResourceFeedback[]>> {
        const request = {
            uploadFieldConfigKey: configKey,
            resources: uploadFiles,
            contextObjects: contextobject,
            javascriptContext: javascriptcontext
        };

        return this.backendService.postData( 'api/v1/cs/sys/fileupload/validation/batchProvideFeedback', request, { blockUI: false } ).pipe(
            map( data => {
                if ( !data ) {
                    return new Map();
                }

                return new Map( Object.entries( data ) );
            } )
        );
    }

    public registerFeedbackRequest( uploadFileUuid: string, feedbackRequestSubscription: Subscription ): void {
        this.ongoingFeedbackRequests.set( uploadFileUuid, feedbackRequestSubscription );
    }

    public abortFeedbackRequest( uploadFileUuid: string ): void {
        if ( !this.ongoingFeedbackRequests.get( uploadFileUuid )?.closed ) {
            this.ongoingFeedbackRequests.get( uploadFileUuid )?.unsubscribe();
        }
        this.ongoingFeedbackRequests.delete( uploadFileUuid );
    }

    public cleanupFeedback( currentModel: HisUploadFile[] ) {
        const currentUuids = new Set( _.isEmpty( currentModel ) ? [] : currentModel.map( file => file.uuid ) );
        [...this.ongoingFeedbackRequests.keys()]
            .filter( uuid => !currentUuids.has( uuid ) )
            .forEach( uploadFileUuid => this.abortFeedbackRequest( uploadFileUuid ) );
    }

    public abortAllOnGoingFeedbackRequests() {
        this.ongoingFeedbackRequests.forEach( request => request.unsubscribe() );
        this.ongoingFeedbackRequests.clear();
    }

    public isFeedbackRequestRunning( uploadFileUuid: string ): boolean {
        return this.ongoingFeedbackRequests.has( uploadFileUuid );
    }
}
